<template>
  <v-dialog v-model="modal" persistent width="1000px">
    <v-card>
      <v-card-title> Ajouter une annexe </v-card-title>
      <v-card-text>
        <v-form ref="form" :readonly="working" @submit.stop.prevent>
          <v-container fluid>
            <v-row dense>
              <v-col cols="12">
                <p class="accent-block">
                  Choisissez une annexe à rajouter en sélectionnant son type ainsi que la
                  date associée et son sous-type si nécessaire.
                </p>
              </v-col>
              <v-col cols="6">
                <a-type-document-select
                  :disabled="typeDisabled"
                  :avecObservations="avecObservations"
                  :typesDocuments="typesDocuments"
                  dense
                  filled
                  label="Type de l'annexe"
                  v-model="type"
                  :rules="[requiredRule(), serverRule('type')]"
                  data-testid="type-annexe"
                />
              </v-col>
              <v-col cols="6">
                <v-fade-transition>
                  <v-text-field
                    v-if="libelleDate"
                    dense
                    filled
                    :label="libelleDate"
                    type="date"
                    v-model="date"
                    :rules="[dateRule(date), requiredRule(), serverRule('date')]"
                    data-testid="date-document"
                  />
                </v-fade-transition>
              </v-col>

              <v-expand-transition>
                <template v-if="assembleGenerale">
                  <v-col cols="12">
                    <a-type-assemble-generale-select
                      dense
                      filled
                      label="Sous-type"
                      v-model="extraordinaire"
                      data-testid="sous-type-assemblee-generale"
                    />
                  </v-col>
                </template>
              </v-expand-transition>

              <v-expand-transition>
                <template v-if="decisionJudiciaireAdmin">
                  <v-col cols="12">
                    <a-sous-type-document-switch
                      v-model="judiciaire"
                      vrai="JUDICIAIRE"
                      faux="ADMIN"
                      data-testid="sous-type-document"
                    />
                  </v-col>
                </template>
              </v-expand-transition>
              <v-expand-transition>
                <template v-if="extraitPublicationJAL">
                  <v-col cols="12">
                    <v-select
                      label="Sous-type"
                      dense
                      filled
                      :rules="[requiredRule(), serverRule('sousType')]"
                      v-model="extraitpub"
                      :items="sousTypesExtraitPub"
                      data-testid="sous-type-extrait-publication-jal"
                    />
                  </v-col>
                </template>
              </v-expand-transition>

              <v-expand-transition>
                <template v-if="necessiteJournal">
                  <v-col cols="12">
                    <a-journal-annonce-combobox
                      dense
                      filled
                      label="Journal d'annonces légales"
                      v-model="journal"
                      :codePostal="codePostal"
                      :extraitpub="extraitpub"
                      :rules="[serverRule('journal')]"
                      data-testid="journal-annonces-legales"
                    />
                  </v-col>
                </template>
              </v-expand-transition>

               <v-expand-transition>
                <template v-if="necessiteSousTypeDeclaration">
                  <v-col cols="12">
                    <v-select
                      label="Sous-type"
                      dense
                      filled
                      :rules="[requiredRule(), serverRule('sousType')]"
                      v-model="sousType"
                      :items="sousTypesDeclaration"
                      data-testid="sous-type-declaration"
                    />
                  </v-col>
                </template>
              </v-expand-transition>

              <v-col cols="12">
                <v-file-input
                  data-testid="fichier"
                  dense
                  filled
                  label="Fichier"
                  :accept="acceptes"
                  v-model="fichier"
                  validate-on-blur
                  :rules="[
                    fichierNonVide(),
                    typeFichier(),
                    tailleFichier(),
                    serverRule('fichier'),
                  ]"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn data-testid="valider-ajouter-document" color="primary" @click="ajoute" :loading="working"> Ajouter </v-btn>
        <v-btn data-testid="annuler-ajouter-document" @click="ferme" :disabled="working"> Annuler </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { libelleDateDocument, sousTypeDocument } from "@/filters/typedocument";
import makeRules from "@/mixins/makeRules";
import { VuexSnackbar } from "@/mixins/makeVuexSnackbar";
import { mapGetters } from "vuex";
import makeRemoteWorker from "@/mixins/makeRemoteWorker";
import formatTaille from "@/filters/bytes";

export default {
  mixins: [
    VuexSnackbar,
    makeRules({ inject: false }),
    makeRemoteWorker({ provide: false })
  ],

  props: {
    typesDocuments: {
      type: String,
      required: false,
    },
    codePostal: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      working: false,
      modal: false,
      date: null,
      type: null,
      sousType: null,
      fichier: null,
      journal: null,
      extraordinaire: null,
      judiciaire: null,
      fichierOptionel: true,
      extraitpub: null,
      sousTypesExtraitPub: [
        "PUBCRE",
        "PUBDIS",
        "PUBFUS",
      ].map(value => ({ value, text: sousTypeDocument(value) })),
      sousTypesDeclaration: [
        "CREATION",
        "MODIFICATION",
        "DISSOLUTION",
        "RADIATION",
        "FUSION_ABSORPTION"
      ].map(value => ({ value, text: sousTypeDocument(value) })),
    };
  },

  methods: {
    ferme() {
      this.modal = false;
    },
    fichierNonVide() {
      return value =>
        this.fichierOptionel || value?.size > 0 || "le champ est obligatoire";
    },

    typeFichier() {
      return value =>
        !value ||
        this.acceptes.includes(value.type) ||
        "le type de fichier n'est pas acceptable";
    },

    tailleFichier() {
      return value =>
        !value ||
        this.tailleFichierMax > value.size ||
        "le fichier dépasse la taille maximale autorisée de " +
          formatTaille(this.tailleFichierMax);
    },

    nouveau(promiseUpload, callback, doc = {}) {
      this.ouvre(doc, promiseUpload, callback);
    },

    ouvre(doc, promiseUpload, callback) {
      this.extraordinaire = "ORDINAIRE";
      this.judiciaire = "ADMIN";
      this.extraitpub = "PUBCRE";

      this.date = null;
      this.type = doc.type;
      this.fichier = null;
      this.journal = null;
      this.sousType = "CREATION";

      this.callback = callback;
      this.promiseUpload = promiseUpload;
      this.modal = true;
      this.$nextTick(() => {
        this.$refs.form.resetValidation();
      });
    },

    ajoute() {
      const promiseUpload = this.promiseUpload;
      const callback = this.callback;
      this.fichierOptionel = false;

      let sousType = null;
      if (this.assembleGenerale) {
        sousType = this.extraordinaire;
      } else if (this.decisionJudiciaireAdmin) {
        sousType = this.judiciaire;
      } else if (this.extraitPublicationJAL) {
        sousType = this.extraitpub;
      } else if (this.declarationInscription) {
        sousType = this.sousType;
      }

      let date = null;
      if (this.libelleDate) {
        date = this.date;
      }

      let journal = null;
      if (this.necessiteJournal) {
        journal = this.journal;
      }

      const annexes = {
        fichier: this.fichier,
        nom: this.fichier?.name,
        taille: this.fichier?.size,
        journal,
        type: this.type,
        sousType,
        date,
      };

      this.doWork({
        forms: [this.$refs.form],
        work() {
          return promiseUpload(annexes);
        },
        callback(response) {
          this.modal = false;
          callback(response);
        },
        successText: "Le document a été ajouté.",
        errorText: "Erreur durant l'ajout du document.",
      });
    },
  },

  computed: {
    typeDisabled() {
      return this.avecObservations;
    },
    avecObservations() {
      return  ["OBSERVATIONS", "PV_AUDITION"].includes(this.type);
    },
    necessiteJournal() {
      return this.extraitPublicationJAL || "JUSTIFICATIF_PAIEMENT" === this.type;
    },
    extraitPublicationJAL() {
      return "EXTRAIT_PUB_JAL" === this.type;
    },
    necessiteSousTypeDeclaration() {
      return this.declarationInscription && "pieces" === this.typesDocuments;
    },
    declarationInscription() {
      return "DECLARATION_INSCR" === this.type;
    },
    decisionJudiciaireAdmin() {
      return "DECISION_JUDICIAIRE" === this.type;
    },
    assembleGenerale() {
      return "PV_AG" === this.type;
    },
    libelleDate() {
      return libelleDateDocument(this.type);
    },
    ...mapGetters({
      acceptes: "fichiers/mimeAcceptes",
      tailleFichierMax: "fichiers/tailleFichierMax",
    }),
  },
};
</script>
